import Modal, { ModalFooter } from "@amzn/meridian/modal";
import React, { useEffect, useState } from "react";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Input from "@amzn/meridian/input";
import Text from "@amzn/meridian/text";
import { Space } from "../../../space/Space";
import Box from "@amzn/meridian/box";
import { UpdateAppealRequest, useUpdateAppealMutation } from "../../../../endpoints/updateAppeal";
import { useAppSelector } from "../../../../app/store/hooks";
import { coverPageConfig } from "./formConfig";
import { CoverPageData, DEFAULT_COVER_PAGE, EditCoverPageProps } from "./types";

export const EditCoverPage: React.FC<EditCoverPageProps> = ({
    open,
    onClose,
    immutableInitialData,
}) => {
    const [coverPageData, setCoverPageData] = useState<CoverPageData>(() => {
        const initialCoverPage =
            immutableInitialData?.appealDetails?.appealApproachCalculationData?.exportAppealsPackage
                ?.coverPage || {};

        return {
            ...DEFAULT_COVER_PAGE,
            ...initialCoverPage,
            subjectPropertyAddress: {
                ...DEFAULT_COVER_PAGE.subjectPropertyAddress,
                ...initialCoverPage.subjectPropertyAddress,
            },
            amazonAddress: {
                ...DEFAULT_COVER_PAGE.amazonAddress,
                ...initialCoverPage.amazonAddress,
            },
        };
    });

    const [updateAppeal, { isLoading }] = useUpdateAppealMutation();
    const { alias } = useAppSelector(state => state.auth.user);

    useEffect(() => {
        if (
            immutableInitialData?.appealDetails?.appealApproachCalculationData?.exportAppealsPackage
                ?.coverPage
        ) {
            const initialData =
                immutableInitialData.appealDetails.appealApproachCalculationData
                    .exportAppealsPackage.coverPage;
            setCoverPageData(prevData => ({
                ...prevData,
                ...initialData,
                subjectPropertyAddress: {
                    ...prevData.subjectPropertyAddress,
                    ...initialData.subjectPropertyAddress,
                },
                amazonAddress: {
                    ...prevData.amazonAddress,
                    ...initialData.amazonAddress,
                },
            }));
        }
    }, [immutableInitialData]);

    const handleInputChange = (
        field: string,
        value: string,
        nested?: { parent: keyof CoverPageData; field: string }
    ) => {
        setCoverPageData(prev => {
            if (nested) {
                return Object.assign({}, prev, {
                    [nested.parent]: Object.assign({}, prev[nested.parent], {
                        [nested.field]: value,
                    }),
                });
            }

            return Object.assign({}, prev, { [field]: value });
        });
    };

    const handleSave = async () => {
        try {
            const updateData: UpdateAppealRequest = {
                businessUseCase: "SMART",
                appealArtifactId: immutableInitialData.appealArtifactId,
                appealDetails: {
                    ...immutableInitialData.appealDetails,
                    appealApproachCalculationData: {
                        ...immutableInitialData.appealDetails.appealApproachCalculationData,
                        exportAppealsPackage: {
                            ...immutableInitialData.appealDetails.appealApproachCalculationData
                                ?.exportAppealsPackage,
                            coverPage: coverPageData,
                        },
                    },
                },
                appealStatus: immutableInitialData.appealDetails.status,
                clientName: alias,
            };

            const result = await updateAppeal(updateData).unwrap();
            if (result.status === "SUCCEEDED") {
                onClose();
            } else {
                console.error("Failed to update appeal");
            }
        } catch (error) {
            console.error("Error updating appeal:", error);
        }
    };

    const renderInputField = (fieldConfig: any, index: number) => {
        const value = fieldConfig.nested
            ? coverPageData[fieldConfig.nested.parent][fieldConfig.nested.field]
            : coverPageData[fieldConfig.field];

        return (
            <React.Fragment key={index}>
                {fieldConfig.groupLabel && (
                    <>
                        <Text type="h400">{fieldConfig.groupLabel}</Text>
                        <Space size="xs" />
                    </>
                )}
                <Box width={fieldConfig.nested?.parent === "amazonAddress" ? "30%" : "45%"}>
                    <Text>{fieldConfig.label}</Text>
                    <Input
                        value={value}
                        onChange={value =>
                            handleInputChange(fieldConfig.field, value, fieldConfig.nested)
                        }
                        placeholder={fieldConfig.placeholder}
                        type={fieldConfig.type}
                    />
                </Box>
            </React.Fragment>
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Edit Cover Page"
            width="30vw"
            aria-describedby="EditCoverModal"
        >
            {coverPageConfig.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                    <Row alignmentHorizontal="justify">
                        {row.map((field, fieldIndex) => renderInputField(field, fieldIndex))}
                    </Row>
                    <Space size="s" />
                </React.Fragment>
            ))}

            <ModalFooter>
                <Row alignmentHorizontal="justify">
                    <Button type="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isLoading}>
                        {isLoading ? "Saving..." : "Save"}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
